/* eslint-disable jira/filename-case */
// eslint-disable-next-line jira/restricted/cookie -- This reduced TS complexity by 3,114,190 across Jira
import Cookies from 'js-cookie';

/*
This logic was ported from MobileUtils class from jira monolith to
provide the same conditions for checking whether the user agent is mobile or not.
*/

const DESKTOP_SWITCH_COOKIE_KEY = 'jira.mobile.desktop.switch';
const MOBILE_USER_AGENT_REGEX = /Android|iPhone|iPad|Mobil/i;
const MOBILE_STRING_IN_USER_AGENT = new RegExp('Mobile');
const IPAD_USER_AGENT = new RegExp('iPad;');
const IOS_USER_AGENT = new RegExp('(iPhone;|iPad;|iPod;)');
const ANDROID_USER_AGENT = new RegExp('Android');

// This regex matches the following iOS rendering engines:
// 1. Chrome for iOS
// 2. Firefox for iOS
// 3. Edge for iOS
const NON_SAFARI_IOS_RENDERING_ENGINE_USER_AGENT = new RegExp('CriOS|FxiOS|EdgiOS');

// Fallback for SSR where navigator.userAgent is not available
let userAgentHeader: string | undefined;

/**
 * Sets the user agent with the HTTP request header.
 * This function is only called by @jira/ssr__frontend-server#src/multi-spa-route.tsx
 * which passes in request.headers['user-agent'].
 */
export function setUserAgentFromRequestHeader(userAgent: string | undefined): void {
	userAgentHeader = userAgent;
}

/**
 * Determines if the current user agent is supported based on specific criteria.
 * It checks if the execution is server-side, the presence and validity of the navigator
 * object, and matches against known patterns for Android Chrome and mobile user agents,
 * excluding old Android versions.
 */
export const isSupportedUserAgent = (): boolean => {
	const userAgent = globalThis.navigator?.userAgent ?? userAgentHeader;

	return userAgent !== undefined && MOBILE_USER_AGENT_REGEX.test(userAgent);
};

export const isAndroidUserAgent: () => boolean = () => {
	const userAgent = globalThis.navigator?.userAgent ?? userAgentHeader;

	return userAgent !== undefined && ANDROID_USER_AGENT.test(userAgent);
};

export const isIosUserAgent: () => boolean = () => {
	const userAgent = globalThis.navigator?.userAgent ?? userAgentHeader;

	return userAgent !== undefined && IOS_USER_AGENT.test(userAgent);
};

/**
 * Checks whether the device's user agent suggests it's a tablet by comparing it
 * against known tablet patterns. Specifically, it identifies iPads and Android
 * tablets, ensuring they are not confused with mobile phones.
 */
export const isTabletUserAgent = (): boolean => {
	const userAgent = globalThis.navigator?.userAgent ?? userAgentHeader;

	if (!userAgent) {
		return false;
	}

	return (
		IPAD_USER_AGENT.test(userAgent) ||
		(isAndroidUserAgent() && !MOBILE_STRING_IN_USER_AGENT.test(userAgent))
	);
};

const isDesktopSwitchEnabled = () => Cookies.get(DESKTOP_SWITCH_COOKIE_KEY) === 'true';
const isMobileUserAgent = (): boolean => isSupportedUserAgent() && !isDesktopSwitchEnabled();

// it is not possible to detect Safari on iOS directly
// so we need to exclude all other iOS rendering engines
// https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent
export const isSafariIosRenderingEngine = (): boolean => {
	const userAgent = globalThis.navigator?.userAgent ?? userAgentHeader;

	if (!userAgent) {
		return false;
	}

	return isIosUserAgent() && !NON_SAFARI_IOS_RENDERING_ENGINE_USER_AGENT.test(userAgent);
};

export const shouldShowReturnToMobileBanner = (): boolean => {
	return isSupportedUserAgent() && !isSafariIosRenderingEngine();
};

export const shouldShowDismissButton = (): boolean =>
	shouldShowReturnToMobileBanner() && isTabletUserAgent();

export const determineOperatingSystemAttribute = (): 'ios' | 'android' | 'other' => {
	if (isIosUserAgent()) {
		return 'ios';
	}
	if (isAndroidUserAgent()) {
		return 'android';
	}
	return 'other';
};

export default isMobileUserAgent;
